import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { 
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MergeIcon from '@mui/icons-material/Merge';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import ArticleIcon from '@mui/icons-material/AccountTree';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Document as AppDocument } from '../../types/Document';
import { Category } from '../../types/Category';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import DuplicateDocumentDialog from '../dialogs/DuplicateDocumentDialog';
import MergeDocumentDialog from '../dialogs/MergeDocumentDialog';
import { RenameDocumentDialog } from '../dialogs/RenameDocumentDialog';
import { ChooseDocumentNameDialog } from '../dialogs/ChooseDocumentNameDialog';
import SuggestedTreeDialog from '../dialogs/SuggestedTreeDialog';
import SuggestTreeContextDialog from '../dialogs/SuggestTreeContextDialog';
import ExpandTreeDialog from '../dialogs/ExpandTreeDialog';
import ExpandTreeResultsDialog from '../dialogs/ExpandTreeResultsDialog';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import { useDocument } from '../../contexts/DocumentContext';
import { useHelpContent } from '../../utils/HelpUtils';
import { HelpContentKey } from '../../constants/helpContent';
import { extractDocumentName } from '../../utils/DocumentUtils';
import api from '../../services/api';

// Configure PDF.js to use the local worker file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const DrawerWidth = 280;

const StyledDrawer = styled(Drawer)({
  width: DrawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    padding: '8px 4px',
    width: DrawerWidth,
    boxSizing: 'border-box',
    marginTop: '64px',
    backgroundColor: '#2c3e50',
    borderRight: 'none',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    // Add these properties to handle overflow properly:
    height: 'calc(100vh - 64px)', // Subtract the top margin
    overflowY: 'auto', // Allow scrolling if content is too tall
    overflowX: 'hidden',
  },
});

const StyledListItemButton = styled(ListItemButton)({
  padding: '12px 24px',
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
});

const StyledListItemIcon = styled(ListItemIcon)({
  color: '#ffffff',
  minWidth: '40px',
});

const StyledListItemText = styled(ListItemText)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
  maxWidth: '200px', // adjust as needed for icons and padding
});

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    backgroundColor: '#ffffff',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
    borderRadius: '4px',
    marginTop: '48px',
    minWidth: '200px',
    maxWidth: '200px',
    width: '200px',
    position: 'absolute',
  },
  '& .MuiListItemIcon-root': {
    minWidth: '32px',
    color: '#666',
  },
});

const StyledMenuItem = styled(MenuItem)({
  padding: '8px 16px',
  fontSize: '14px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
  boxSizing: 'border-box',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    minWidth: '400px',
  },
});

const DocumentButton = styled(ListItemButton)({
  padding: '8px 24px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(144, 202, 249, 0.22)',
    borderLeft: '4px solid rgba(144, 202, 249, 0.9)',
    paddingLeft: '20px', // compensate for left border so text aligns
    '&:hover': {
      backgroundColor: 'rgba(144, 202, 249, 0.3)',
    },
  },
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const DocumentIcon = styled(ListItemIcon)({
  color: '#ffffff',
  minWidth: '40px',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#90caf9',
  },
});

const DocumentText = styled(ListItemText)({
  '& .MuiListItemText-primary': {
    color: '#ffffff',
    fontSize: '0.95rem',
  },
  '& .MuiListItemText-primary.MuiTypography-root': {
    fontWeight: 600,
  },
  flex: 1,
  cursor: 'pointer',
});

const DocumentList = styled(List)({
   marginTop: 0, // no outer spacing
   padding: 0,
   flexShrink: 1,
   overflowY: 'auto',
   maxHeight: '100%', // allows for scroll behavior if needed
});

const OpenDocumentsSection = styled(Box)({
  backgroundColor: 'rgba(76, 175, 80, 0.1)', // Light green background
  borderRadius: '4px',
  margin: '4px 8px',
  padding: '4px',
});

const ClosedDocumentsSection = styled(Box)({
  backgroundColor: 'rgba(158, 158, 158, 0.1)', // Light gray background
  borderRadius: '4px',
  margin: '4px 8px',
  padding: '4px',
});

const MenuSection = styled(Box)({
  backgroundColor: 'rgba(52, 152, 219, 0.1)', // Light blue background
  borderRadius: '4px',
  margin: '4px 8px',
  padding: '4px',
});

const CompactTypography = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 600,
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  padding: '8px 12px',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '4px 4px 0 0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const MenuButton = styled(Button)({
  width: '100%',
  justifyContent: 'space-between',
  padding: '12px 24px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '8px',
  margin: '0', // Remove the margin since MenuSection handles it
  color: '#ffffff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiButton-startIcon': {
    marginRight: '8px',
  },
  '& .MuiButton-endIcon': {
    marginLeft: '8px',
    transition: 'transform 0.2s ease',
  },
  '&.menu-open .MuiButton-endIcon': {
    transform: 'rotate(180deg)',
  },
});

const StyledAccordion = styled(Accordion)({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '32px',
    padding: '0 8px',
    '&.Mui-expanded': {
      minHeight: '32px',
    },
  },
  '& .MuiAccordionSummary-content': {
    margin: '4px 0',
    '&.Mui-expanded': {
      margin: '4px 0',
    },
  },
  '& .MuiAccordionDetails-root': {
    padding: '4px 0',
  },
});

const StyledChip = styled(Chip)({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
  fontSize: '0.8rem',
  height: '24px',
  margin: '2px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
});

interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface DocumentPanelProps {
  onNewDocument: (documentName: string, open: boolean) => Promise<any>;
  documents: AppDocument[];
  onOpenDocument: (document: AppDocument) => Promise<void>;
  onCloseDocument: (document: AppDocument) => void;
  onDeleteDocument: (documentId: string) => void;
  onRenameDocument: (documentId: string, newName: string) => void;
  onDuplicateDocument: (documentId: string, newName: string, selectedSubcases: string[]) => void;
  onMergeDocument: (documentId: string, mergeFromDocumentId: string, newName: string, resolveMissing: MergeMissingResolve, resolveTypeMismatch: MergeTypeMismatchResolve) => void;
  onSelectDocument: (document?: AppDocument) => void;
  onLoadFromFile: (fileName: string, file?: File) => void;
  onExportDocument: (documentId: string) => void;
  currentTab: string | null;
  currentDocument: AppDocument | undefined;
  setGlobalLoading: (loading: boolean) => void;
  tabs: Tab[];
  refreshCurrentDocument?: () => Promise<void>;
  refreshDocuments?: () => Promise<void>;
}

const DocumentPanel: React.FC<DocumentPanelProps> = ({
  onNewDocument,
  documents,
  onOpenDocument,
  onCloseDocument,
  onDeleteDocument,
  onRenameDocument,
  onDuplicateDocument,
  onMergeDocument,
  onSelectDocument,
  onLoadFromFile,
  onExportDocument,
  currentTab,
  currentDocument,
  setGlobalLoading,
  tabs,
  refreshCurrentDocument,
  refreshDocuments
}) => {
  const { suggestTreeFromText, createDocument, expandTreeFromText } = useDocument();
  const { getTooltipTextForState, getMenuTooltipTextForState, helpMode, shouldShowTooltip } = useHelpContent();
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [openMergeDialog, setOpenMergeDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openSuggestTreeDialog, setOpenSuggestTreeDialog] = useState(false);
  const [openExpandTreeDialog, setOpenExpandTreeDialog] = useState(false);
  const [openExpandTreeResultsDialog, setOpenExpandTreeResultsDialog] = useState(false);
  const [newDocumentName, setNewDocumentName] = useState('');
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [documentToDuplicate, setDocumentToDuplicate] = useState<AppDocument | null>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [suggestedTree, setSuggestedTree] = useState<any>(null);
  const [expansionResult, setExpansionResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [contextMenuDocument, setContextMenuDocument] = useState<AppDocument | null>(null);
  const [openDocumentsExpanded, setOpenDocumentsExpanded] = useState(true);
  const [closedDocumentsExpanded, setClosedDocumentsExpanded] = useState(true);
  const [documentToRename, setDocumentToRename] = useState<string | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [suggestTreeFiles, setSuggestTreeFiles] = useState<File[]>([]);
  const [suggestTreeFileNames, setSuggestTreeFileNames] = useState<string[]>([]);
  const [suggestTreeContext, setSuggestTreeContext] = useState('');
  const [openContextDialog, setOpenContextDialog] = useState(false);
  const [pendingContextFiles, setPendingContextFiles] = useState<File[]>([]);
  const [pendingContextFileNames, setPendingContextFileNames] = useState<string[]>([]);
  const [suggestTreeMaxDepth, setSuggestTreeMaxDepth] = useState<number>(1); // Track maxDepth from context dialog
  const [expandTreeFiles, setExpandTreeFiles] = useState<File[]>([]);
  const [expandTreeFileNames, setExpandTreeFileNames] = useState<string[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const suggestTreeFileInputRef = React.useRef<HTMLInputElement>(null);
  const expandTreeFileInputRef = React.useRef<HTMLInputElement>(null);

  const normalize = (str: string) =>
    str.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase();

  const isDuplicateName = (name: string, excludeId?: string) => {
    const normalizedName = normalize(name);
    const result = documents.some(doc => {
      const docLabel = normalize(doc.label || '');
      const isDuplicate = docLabel === normalizedName && (!excludeId || doc.id !== excludeId);
      return isDuplicate;
    });
    return result;
  };

  const handleNameChange = (value: string, operation: 'new' | 'rename' | 'duplicate') => {
    const trimmedValue = value.trim();
    const excludeId = (operation === 'rename' && documentToRename) ? documentToRename :
                      (operation === 'duplicate' && documentToDuplicate) ? documentToDuplicate.id : undefined;
    if (isDuplicateName(trimmedValue, excludeId)) {
      setNameError('A document with this name already exists');
    } else {
      setNameError('');
    }
    switch (operation) {
      case 'new':
        setNewDocumentName(value);
        break;
      case 'rename':
        setNewName(value);
        break;
      case 'duplicate':
        setDuplicateName(value);
        break;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNewDocument = () => {
    setOpenDialog(true);
    handleClose();
  };

  const handleOpenClick = async () => {
       if (currentDocument && !currentDocument.hasOpenTab) {
        await onOpenDocument(currentDocument);
        handleClose();
       }
    };

  const handleCloseClick = () => {
      if (currentDocument && currentDocument.hasOpenTab ) {
         onCloseDocument(currentDocument);
         handleClose();
      }
    };

  const handleDeleteDocument = () => {
    if (documentToDelete) {
      onDeleteDocument(documentToDelete);
      setOpenDeleteDialog(false);
      setDocumentToDelete(null);
      handleClose();
    }
  };

  const handleDeleteClick = () => {
    if (currentDocument) {
      setDocumentToDelete(currentDocument.id);
      setOpenDeleteDialog(true);
    }
    // Don't call handleClose() here - let the dialog handle its own state
    // The menu will close automatically when a menu item is clicked
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewDocumentName('');
    setNameError('');
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleCreateDocument = () => {
    const trimmedName = newDocumentName.trim();
    if (!trimmedName) {
      console.log('Empty name, setting error');
      setNameError('Document name cannot be empty');
      return;
    }
    if (isDuplicateName(trimmedName)) {
      console.log('Duplicate name detected, setting error');
      setNameError('A document with this name already exists');
      return;
    }
    console.log('Creating document:', trimmedName);
    onNewDocument(trimmedName, true);
    handleDialogClose();
  };

  const handleRenameClick = () => {
     if (currentDocument) {
        const document = documents.find(doc => doc.id === currentDocument.id);
        if (document) {
            setDocumentToRename(document.name);
            setNewName(document.name);
            setOpenRenameDialog(true);
        }
     }
    handleClose();
  };

  const handleRenameDocument = (newName: string) => {
    const excludeId = documentToRename ? documentToRename : undefined;
    if (documentToRename) {
      onRenameDocument(documentToRename, newName);
      setOpenRenameDialog(false);
      setDocumentToRename(null);
      setNewName('');
      setNameError('');
      handleClose();
    }
  };

  const handleRenameDialogClose = () => {
    setOpenRenameDialog(false);
    setDocumentToRename(null);
    setNewName('');
    setNameError('');
  };

  const handleDuplicateClick = () => {
    console.log(documents);

   if (currentDocument) {
      const document = documents.find(doc => doc.id === currentDocument.id);
      if (document) {
        setDocumentToDuplicate(document);
        setOpenDuplicateDialog(true);
      }
    }
    handleClose();
  };

  const handleDuplicateConfirm = async (newName: string, selectedSubcases: string[]) => {
    if (documentToDuplicate) {
      onDuplicateDocument(documentToDuplicate.id, newName, selectedSubcases);
      setOpenDuplicateDialog(false);
      setDocumentToDuplicate(null);
    }
  };

  const handleDuplicateDialogClose = () => {
    setOpenDuplicateDialog(false);
    setDocumentToDuplicate(null);
    setDuplicateName('');
    setNameError('');
  };

  const handleLoadFromFileClick = () => {
    if (fileInputRef) {
      fileInputRef.current?.click();
    }
    handleClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (file) {
          if (file.name.endsWith('.nbr')) {
            try {
              // 1. Read file bytes
              const arrayBuffer = await file.arrayBuffer();
              const documentName = extractDocumentName(new Uint8Array(arrayBuffer));

              // 2. Check for duplicate names using extracted name
              if (isDuplicateName(documentName)) {
                setUploadFile(file);
                setUploadFileName(documentName);
                setOpenUploadDialog(true);
              } else {
                onLoadFromFile(documentName, file);
              }
            } catch (error) {
              console.error("Error extracting document name:", error);
            }
          } else {
            console.error("Invalid file type. Only .nbr files are allowed.");
          }
        }
      }
    }

    // Reset the input value so the same files can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const handleMergeClick = () => {
    setOpenMergeDialog(true);
    handleClose();
  };

  const handleMerge = async (mergeFromDocumentId: string, newName: string, resolveMissing: MergeMissingResolve, resolveTypeMismatch: MergeTypeMismatchResolve) => {
    if (!currentDocument) return;
    onMergeDocument(currentDocument.id, mergeFromDocumentId, newName, resolveMissing, resolveTypeMismatch);
  };

  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
    setUploadFileName('');
    setUploadFile(null);
  };

  const handleUploadDialogConfirm = (newName: string) => {
    if (uploadFile) {
      onLoadFromFile(newName, uploadFile);
    }
    handleUploadDialogClose();
  };

  // Utility to extract text from file
  const extractTextFromFile = async (file: File): Promise<string> => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'docx') {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } else if (ext === 'pdf') {
     const arrayBuffer = await file.arrayBuffer();
       const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

       const allText: string[] = [];

       for (let i = 1; i <= pdf.numPages; i++) {
         const page = await pdf.getPage(i);
         const content = await page.getTextContent();

         // Group items into lines based on y coordinate
         const linesMap: { [y: number]: string[] } = {};
         const lineTolerate = 2; // Allow small y diffs for same line

         content.items.forEach((item: any) => {
           if (typeof item.str !== 'string' || item.str.trim().length === 0) return;

           const y = Math.round(item.transform[5] / lineTolerate) * lineTolerate;
           if (!linesMap[y]) linesMap[y] = [];
           linesMap[y].push(item.str);
         });

         const sortedLines = Object.entries(linesMap)
           .sort((a, b) => Number(b[0]) - Number(a[0])) // Top-to-bottom
           .map(([_, strs]) => strs.join(' ').trim())
           .filter(line => line.length > 0);

         // Process lines to preserve structure better
         const processedLines: string[] = [];
         
         for (let j = 0; j < sortedLines.length; j++) {
           const line = sortedLines[j];
           const trimmed = line.trim();
           
           if (trimmed.length === 0) continue;

           // Check if this line might be a heading (all caps, shorter length, or starts with numbers)
           const isLikelyHeading = (
             trimmed === trimmed.toUpperCase() && trimmed.length < 50 ||
             /^\d+\.\s/.test(trimmed) ||
             /^[A-Z][A-Z\s]{2,}$/.test(trimmed) ||
             trimmed.length < 30 && /^[A-Z]/.test(trimmed)
           );

           // Check if this line might be a section break
           const isSectionBreak = (
             trimmed.length < 20 && /^[A-Z\s]+$/.test(trimmed) ||
             /^[IVX]+\./.test(trimmed) ||
             /^[A-Z]\.\s/.test(trimmed)
           );

           if (isLikelyHeading || isSectionBreak) {
             // Add extra spacing for headings to help AI identify structure
             processedLines.push('');
             processedLines.push(trimmed);
             processedLines.push('');
           } else {
             // For regular text, preserve line breaks to maintain paragraph structure
             processedLines.push(trimmed);
           }
         }

         allText.push(...processedLines);
       }

       const finalText = allText.join('\n');
       return finalText;
    } else {
      // Assume text file
      return await file.text();
    }
  };

  const handleSuggestTreeClick = () => {
    if (suggestTreeFileInputRef) {
      suggestTreeFileInputRef.current?.click();
    }
    handleClose();
  };

  const handleSuggestTreeFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Convert FileList to array and save for context dialog
      const fileArray = Array.from(files);
      setPendingContextFiles(fileArray);
      setPendingContextFileNames(fileArray.map(file => file.name));
      setOpenContextDialog(true);
    }
    if (suggestTreeFileInputRef.current) {
      suggestTreeFileInputRef.current.value = '';
    }
  };

  const handleSuggestTreeRetry = async (maxDepth: number) => {
    if (suggestTreeFiles.length > 0) {
      try {
        setGlobalLoading(true);
        setError(null);
        
        // Extract text from all files
        const textPromises = suggestTreeFiles.map(file => extractTextFromFile(file));
        const texts = await Promise.all(textPromises);
        
        // Combine all texts with file names as separators
        const combinedText = texts.map((text, index) => {
          const fileName = suggestTreeFiles[index].name;
          return `=== FILE: ${fileName} ===\n${text}\n`;
        }).join('\n');
        
        console.log('Combined text for retry:', combinedText);
        
        const result = await suggestTreeFromText(combinedText, maxDepth, suggestTreeContext);
        if (result && result.error) {
          setError(result.error);
          setSuggestedTree(null);
          setOpenSuggestTreeDialog(true);
          return;
        }
        setSuggestedTree(result);
      } catch (err) {
        setError('Failed to process files');
        console.error('Error processing files:', err);
      } finally {
        setGlobalLoading(false);
      }
    }
  };

  const handleSuggestTreeDialogClose = () => {
    setOpenSuggestTreeDialog(false);
    setError(null);
    setSuggestTreeFileNames([]);
  };

  const handleContextDialogClose = () => {
    setOpenContextDialog(false);
    setPendingContextFiles([]);
    setPendingContextFileNames([]);
  };

  const handleContextDialogProceed = async (context: string, maxDepth: number) => {
    try {
      setGlobalLoading(true);
      setError(null);
      
      // Store the maxDepth for use in SuggestedTreeDialog
      setSuggestTreeMaxDepth(maxDepth);
      
      // Combine all file texts
      let combinedText = '';
      for (const file of pendingContextFiles) {
        const fileText = await extractTextFromFile(file);
        combinedText += fileText + '\n\n';
      }
      
      // Call the API to suggest tree
      const result = await suggestTreeFromText(combinedText, maxDepth, context);
      
      if (result && result.tree) {
        setSuggestedTree(result);
        setSuggestTreeFileNames(pendingContextFileNames);
        setSuggestTreeFiles(pendingContextFiles);
        setOpenSuggestTreeDialog(true);
        setOpenContextDialog(false);
        setPendingContextFiles([]);
        setPendingContextFileNames([]);
      } else {
        setError('No tree structure received from the API');
      }
    } catch (error) {
      console.error('Error suggesting tree:', error);
      setError('Failed to suggest tree structure');
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleExpandTreeClick = async () => {
    if (currentDocument) {
      setExpandTreeFiles([]);
      setExpandTreeFileNames([]);
      
      // Trigger file input directly - depth validation will be handled by backend
      if (expandTreeFileInputRef.current) {
        expandTreeFileInputRef.current.click();
      }
    }
  };

  const handleExpandTreeFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setExpandTreeFiles(files);
      setExpandTreeFileNames(files.map(f => f.name));
      setOpenExpandTreeDialog(true);
      handleClose();
    }
    // Reset the input value so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleExpandTreeProceed = async (context: string, maxDepth: number) => {
    if (!currentDocument || expandTreeFiles.length === 0) return;
    
    try {
      setGlobalLoading(true);
      setError(null);
      
      // Combine all file texts
      let combinedText = '';
      for (const file of expandTreeFiles) {
        const fileText = await extractTextFromFile(file);
        combinedText += fileText + '\n\n';
      }
      
      // Call the API to expand tree
      const result = await expandTreeFromText(currentDocument.id, combinedText, maxDepth, context);
      
      if (result) {
        setExpansionResult(result);
        setOpenExpandTreeDialog(false);
        setOpenExpandTreeResultsDialog(true);
      } else {
        setError('No expansion result received from the API');
      }
    } catch (error: any) {
      console.error('Error expanding tree:', error);
      
      // Check if this is a depth validation error
      if (error.message && error.message.includes('Cannot expand document: existing structure has depth')) {
        // Extract depth information from the error message
        const depthMatch = error.message.match(/existing structure has depth (\d+), but you selected max depth (\d+)/);
        if (depthMatch) {
          const existingDepth = parseInt(depthMatch[1]);
          const selectedDepth = parseInt(depthMatch[2]);
          setError(`Depth mismatch: Your document has ${existingDepth} levels, but you selected ${selectedDepth}. Please increase the max depth to at least ${existingDepth}.`);
        } else {
          setError(error.message);
        }
      } else {
        setError('Failed to expand tree structure');
      }
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleExpandTreeDialogClose = () => {
    setOpenExpandTreeDialog(false);
    setExpandTreeFiles([]);
    setExpandTreeFileNames([]);
  };

  const handleExpandTreeResultsDialogClose = () => {
    setOpenExpandTreeResultsDialog(false);
    setExpansionResult(null);
  };

  const handleApplyExpansion = async () => {
    // TODO: Implement the logic to apply the expansion results
    // This would involve creating new categories and placing content
    console.log('Applying expansion:', expansionResult);
    setOpenExpandTreeResultsDialog(false);
    setExpansionResult(null);
  };

  // Context menu handlers
  const handleContextMenu = (event: React.MouseEvent, document: AppDocument) => {
    event.preventDefault();
    setContextMenuAnchorEl(event.currentTarget as HTMLElement);
    setContextMenuDocument(document);
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchorEl(null);
    setContextMenuDocument(null);
  };

  const handleContextMenuOpen = () => {
    if (contextMenuDocument) {
      onOpenDocument(contextMenuDocument);
    }
    handleContextMenuClose();
  };

  const handleContextMenuCloseDoc = () => {
    if (contextMenuDocument) {
      onCloseDocument(contextMenuDocument);
    }
    handleContextMenuClose();
  };

  const handleContextMenuDelete = () => {
    if (contextMenuDocument) {
      setDocumentToDelete(contextMenuDocument.id);
      setOpenDeleteDialog(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuRename = () => {
    if (contextMenuDocument) {
      setDocumentToRename(contextMenuDocument.name);
      setNewName(contextMenuDocument.name);
      setOpenRenameDialog(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuDuplicate = () => {
    if (contextMenuDocument) {
      setDocumentToDuplicate(contextMenuDocument);
      setOpenDuplicateDialog(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuMerge = () => {
    if (contextMenuDocument) {
      setOpenMergeDialog(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuExport = () => {
    if (contextMenuDocument) {
      onExportDocument(contextMenuDocument.id);
    }
    handleContextMenuClose();
  };

  const getSubCategories = async (): Promise<string[]> => {
    const subcategoriesResponse = await api.get('/categories/subcategories', { params: { categoryId: currentDocument?.categoryId } });
    const subcategories = subcategoriesResponse.data;
    return subcategories.map((c: Category) => c.name);
  }

  const [subCategories, setSubCategories] = useState<string[]>([]);

  useEffect(() => {
    if (openRenameDialog || openDuplicateDialog) {
        const fetchSubCategories = async () => {
          const result = await getSubCategories();
          setSubCategories(result);
        };
        fetchSubCategories();
      } else {
        setSubCategories([]); // clear when dialog closes
      }
  }, [openRenameDialog, openDuplicateDialog]); // Runs once when component mounts

  const menuItems: Array<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    tooltipKey: HelpContentKey;
  }> = [
    {
      label: 'New..',
      icon: <AddIcon fontSize="small" />,
      onClick: handleNewDocument,
      tooltipKey: 'new-document' as const
    },
    {
      label: 'Open',
      icon: <ArticleIcon fontSize="small" />,
      onClick: handleOpenClick,
      disabled: !currentDocument || currentDocument.hasOpenTab,
      tooltipKey: 'open-document' as const
    },
    {
      label: 'Close',
      icon: <InsertDriveFileIcon fontSize="small" />,
      onClick: handleCloseClick,
      disabled: !currentDocument || !currentDocument.hasOpenTab,
      tooltipKey: 'close-document' as const
    },
    {
      label: 'Delete',
      icon: <DeleteIcon fontSize="small" />,
      onClick: handleDeleteClick,
      disabled: !currentDocument,
      tooltipKey: 'delete-document' as const
    },
    {
      label: 'Rename..',
      icon: <DriveFileRenameOutlineIcon fontSize="small" />,
      onClick: handleRenameClick,
      disabled: !currentDocument,
      tooltipKey: 'rename-document' as const
    },
    {
      label: 'Duplicate..',
      icon: <ContentCopyIcon fontSize="small" />,
      onClick: handleDuplicateClick,
      disabled: !currentDocument,
      tooltipKey: 'duplicate-document' as const
    },
    {
      label: 'Merge..',
      icon: <MergeIcon fontSize="small" />,
      onClick: handleMergeClick,
      disabled: !currentDocument,
      tooltipKey: 'merge-document' as const
    },
    {
      label: 'Suggest Category Tree from Files...',
      icon: <AutoAwesomeIcon fontSize="small" />,
      onClick: handleSuggestTreeClick,
      tooltipKey: 'suggest-tree' as const
    },
    {
      label: 'Expand Document with Files...',
      icon: <AutoAwesomeIcon fontSize="small" />,
      onClick: handleExpandTreeClick,
      disabled: !currentDocument,
      tooltipKey: 'expand-document' as const
    },
    {
      label: 'Load From File..',
      icon: <UploadFileIcon fontSize="small" />,
      onClick: handleLoadFromFileClick,
      tooltipKey: 'load-from-file' as const
    },
    {
      label: 'Export to File..',
      icon: <DownloadIcon fontSize="small" />,
      onClick: () => {
        if (currentDocument) {
          onExportDocument(currentDocument.id);
        }
      },
      disabled: !currentDocument,
      tooltipKey: 'export-to-file' as const
    },
  ];

  const getMenuItemTooltip = (item: (typeof menuItems)[number]) => {
    if (!shouldShowTooltip()) return '';
    const base = getTooltipTextForState(item.tooltipKey, !item.disabled);
    const docName = currentDocument?.label || currentDocument?.name || 'Unnamed';

    // In Help Mode we want to keep the detailed tooltip content and only append context.
    if (helpMode) {
      if (!currentDocument) {
        // For actions that require a document selection, append a helpful note.
        const needsDoc = [
          'open-document',
          'close-document',
          'delete-document',
          'rename-document',
          'duplicate-document',
          'merge-document',
          'export-to-file',
          'expand-document'
        ].includes(item.tooltipKey);
        return needsDoc ? `${base}\n\nSelect a document in the list first.` : base;
      }
      return `${base}\n\nDocument: ${docName}`;
    }

    // Otherwise, use short, contextual single-line tooltips for document actions
    switch (item.tooltipKey) {
      case 'open-document': {
        if (!currentDocument) return 'Open document — select a document';
        return `Open document ${docName}`;
      }
      case 'close-document': {
        if (!currentDocument) return 'Close document — select a document';
        if (!currentDocument.hasOpenTab) return `Close document ${docName} — not open`;
        return `Close document ${docName}`;
      }
      case 'delete-document': {
        if (!currentDocument) return 'Delete document — select a document';
        return `Delete document ${docName}`;
      }
      case 'rename-document': {
        if (!currentDocument) return 'Rename document — select a document';
        return `Rename document ${docName}`;
      }
      case 'duplicate-document': {
        if (!currentDocument) return 'Duplicate document — select a document';
        return `Duplicate document ${docName}`;
      }
      case 'merge-document': {
        if (!currentDocument) return 'Merge document — select a document';
        return `Merge document ${docName}`;
      }
      case 'export-to-file': {
        if (!currentDocument) return 'Export document — select a document';
        return `Export document ${docName}`;
      }
      case 'expand-document': {
        if (!currentDocument) return 'Expand document — select a document';
        return `Expand document ${docName}`;
      }
      default:
        return base;
    }

  };

  return (
    <StyledDrawer variant="permanent">
      <input
        type="file"
        accept=".nbr"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <input
        type="file"
        accept=".pdf,.txt,.docx,.doc,.rtf,.odt,.md,.html,.htm"
        multiple
        style={{ display: 'none' }}
        ref={suggestTreeFileInputRef}
        onChange={handleSuggestTreeFileChange}
      />
      <input
        type="file"
        accept=".pdf,.txt,.docx,.doc,.rtf,.odt,.md,.html,.htm"
        multiple
        style={{ display: 'none' }}
        ref={expandTreeFileInputRef}
        onChange={handleExpandTreeFileChange}
      />
    <MenuSection>
      <Tooltip
        title={getTooltipTextForState('document-menu', true)}
        placement="right"
      >
        <MenuButton
          onClick={handleClick}
          className={Boolean(anchorEl) ? 'menu-open' : ''}
          startIcon={<MenuIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          variant="outlined"
        >
          Document Menu
        </MenuButton>
      </Tooltip>
    </MenuSection>
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              minWidth: '320px',
              maxWidth: '320px',
              width: '320px',
              overflowX: 'hidden',
            },
          },
        }}
      >
        {menuItems.map((item) => {
          const showTooltip = shouldShowTooltip();
          return (
          <Tooltip 
            key={item.label}
            title={getMenuItemTooltip(item)}
            placement="right"
            disableHoverListener={!showTooltip}
            disableFocusListener={!showTooltip}
            disableTouchListener={!showTooltip}
          >
            <span>
              <StyledMenuItem 
                onClick={item.onClick}
                disabled={item.disabled}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </StyledMenuItem>
            </span>
          </Tooltip>
          );
        })}
      </StyledMenu>

      <StyledDialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>New Document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newDocumentName}
            onChange={(e) => handleNameChange(e.target.value, 'new')}
            error={!!nameError}
            helperText={nameError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleCreateDocument}
            variant="contained"
            color="primary"
            disabled={!newDocumentName.trim() || !!nameError}
          >
            Create
          </Button>
        </DialogActions>
      </StyledDialog>

      <StyledDialog open={openDeleteDialog} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{documents.find(doc => doc.id === documentToDelete)?.label}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button 
            onClick={handleDeleteDocument}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </StyledDialog>

      <DuplicateDocumentDialog
        open={openDuplicateDialog}
        onClose={() => {
          setOpenDuplicateDialog(false);
          setDocumentToDuplicate(null);
        }}
        onConfirm={handleDuplicateConfirm}
        documentName={documentToDuplicate?.label || ''}
        subcases={documentToDuplicate?.subcases || []}
        existingDocumentNames={documents.map(doc => doc.label)}
        documentId={documentToDuplicate?.categoryId || ''}
        subCategories={subCategories}
      />
      <RenameDocumentDialog
          open={openRenameDialog}
          onClose={handleRenameDialogClose}
          onRename={handleRenameDocument}
          originalName={newName}
          existingNames={documents.map(doc => doc.label)}
          subCategories={subCategories}
        />
      <MergeDocumentDialog
        open={openMergeDialog}
        onClose={() => setOpenMergeDialog(false)}
        onMerge={handleMerge}
        documents={documents}
        currentDocument={currentDocument}
      />

      <ChooseDocumentNameDialog
        open={openUploadDialog}
        onClose={handleUploadDialogClose}
        onConfirm={handleUploadDialogConfirm}
        originalName={uploadFileName}
        existingNames={documents.map(doc => doc.label)}
      />

      <StyledDialog open={openSuggestTreeDialog} onClose={handleSuggestTreeDialogClose}>
        <SuggestedTreeDialog
          open={openSuggestTreeDialog}
          onClose={handleSuggestTreeDialogClose}
          onRetry={handleSuggestTreeRetry}
          suggestedTree={suggestedTree}
          fileNames={suggestTreeFileNames}
          loading={loading}
          onOpenDocument={onOpenDocument}
          onNewDocument={onNewDocument}
          error={error}
          initialMaxDepth={suggestTreeMaxDepth}
        />
      </StyledDialog>

      <SuggestTreeContextDialog
        open={openContextDialog}
        onClose={handleContextDialogClose}
        onProceed={handleContextDialogProceed}
        fileNames={pendingContextFileNames}
      />

                          <ExpandTreeDialog
              open={openExpandTreeDialog}
              onClose={handleExpandTreeDialogClose}
              onProceed={handleExpandTreeProceed}
              fileNames={expandTreeFileNames}
              currentDocumentName={currentDocument?.label || ''}
            />

      <ExpandTreeResultsDialog
        open={openExpandTreeResultsDialog}
        onClose={handleExpandTreeResultsDialogClose}
        expansionResult={expansionResult}
        onApplyExpansion={handleApplyExpansion}
        currentDocument={currentDocument}
        existingDocuments={documents}
        onOpenDocument={onOpenDocument}
        refreshCurrentDocument={refreshCurrentDocument}
        onNewDocument={onNewDocument}
        refreshDocuments={refreshDocuments}
      />

      {documents.length > 0 && (
        <>
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />
          <Box display="flex" flexDirection="column" height="100%">
            {/* Open Documents Section */}
            <OpenDocumentsSection>
              <StyledAccordion 
                expanded={openDocumentsExpanded}
                onChange={() => setOpenDocumentsExpanded(!openDocumentsExpanded)}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                >
                  <CompactTypography>
                    <ArticleIcon sx={{ fontSize: '16px', color: 'rgba(76, 175, 80, 0.8)' }} />
                    Open Documents ({documents.filter(doc => doc.hasOpenTab).length})
                  </CompactTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <DocumentList>
                    {[...documents.filter(doc => doc.hasOpenTab)]
                      .sort((a, b) => {
                        const aIdx = tabs.findIndex(tab => tab.id === a.id);
                        const bIdx = tabs.findIndex(tab => tab.id === b.id);
                        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                        if (aIdx !== -1) return -1;
                        if (bIdx !== -1) return 1;
                        return a.label.localeCompare(b.label, undefined, { numeric: true });
                      })
                      .map((doc) => (
                        <DocumentButton
                          key={doc.id}
                          selected={currentDocument && currentDocument.id === doc.id}
                          onClick={() => {
                            if (currentDocument && currentDocument.id === doc.id) {
                              onSelectDocument(undefined);
                            } else {
                              onSelectDocument(doc);
                            }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, doc)}
                          sx={{ padding: '4px 12px', minHeight: '32px' }}
                        >
                          <DocumentIcon onClick={(e) => {
                            e.stopPropagation();
                            if (doc.hasOpenTab) {
                              onCloseDocument(doc);
                            }
                          }}>
                            <ArticleIcon />
                          </DocumentIcon>
                          <DocumentText 
                            primary={doc.label} 
                            sx={{ '& .MuiListItemText-primary': { fontSize: '0.85rem' } }}
                          />
                        </DocumentButton>
                      ))}
                  </DocumentList>
                </AccordionDetails>
              </StyledAccordion>
            </OpenDocumentsSection>

            {/* Closed Documents Section */}
            <ClosedDocumentsSection>
              <StyledAccordion 
                expanded={closedDocumentsExpanded}
                onChange={() => setClosedDocumentsExpanded(!closedDocumentsExpanded)}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                >
                  <CompactTypography>
                    <InsertDriveFileIcon sx={{ fontSize: '16px', color: 'rgba(158, 158, 158, 0.8)' }} />
                    Closed Documents ({documents.filter(doc => !doc.hasOpenTab).length})
                  </CompactTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <DocumentList>
                    {[...documents.filter(doc => !doc.hasOpenTab)]
                      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
                      .map((doc) => {
                        return (
                          <DocumentButton
                            key={doc.id}
                            selected={currentDocument && currentDocument.id === doc.id}
                            onClick={() => {
                              if (currentDocument && currentDocument.id === doc.id) {
                                onSelectDocument(undefined);
                              } else {
                                onSelectDocument(doc);
                              }
                            }}
                            onContextMenu={(e) => handleContextMenu(e, doc)}
                            sx={{ padding: '4px 12px', minHeight: '32px' }}
                          >
                            <DocumentIcon onClick={(e) => {
                              e.stopPropagation();
                              if (!doc.hasOpenTab) {
                                onOpenDocument(doc);
                              }
                            }}>
                              <InsertDriveFileIcon />
                            </DocumentIcon>
                            <DocumentText
                              primary={doc.label}
                              sx={{ '& .MuiListItemText-primary': { fontSize: '0.85rem' } }}
                            />
                          </DocumentButton>
                        );
                      })}
                  </DocumentList>
                </AccordionDetails>
              </StyledAccordion>
            </ClosedDocumentsSection>
          </Box>
        </>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchorEl}
        open={Boolean(contextMenuAnchorEl)}
        onClose={handleContextMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            style: {
              backgroundColor: '#2c3e50',
              color: '#ffffff',
              minWidth: '180px',
              boxShadow: '0px 4px 8px rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <Tooltip title={getMenuTooltipTextForState('open-document', !!(contextMenuDocument && !contextMenuDocument.hasOpenTab))} placement="right">
          <MenuItem 
            onClick={handleContextMenuOpen}
            disabled={!contextMenuDocument || contextMenuDocument.hasOpenTab}
            sx={{ 
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <ArticleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open" />
          </MenuItem>
        </Tooltip>
        <Tooltip title={getMenuTooltipTextForState('close-document', !!(contextMenuDocument && contextMenuDocument.hasOpenTab))} placement="right">
          <MenuItem 
            onClick={handleContextMenuCloseDoc}
            disabled={!contextMenuDocument || !contextMenuDocument.hasOpenTab}
            sx={{ 
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <InsertDriveFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Close" />
          </MenuItem>
        </Tooltip>
        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
        <Tooltip title={getMenuTooltipTextForState('rename-document', !!contextMenuDocument)} placement="right">
          <MenuItem 
            onClick={handleContextMenuRename}
            sx={{ 
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Rename..." />
          </MenuItem>
        </Tooltip>
        <Tooltip title={getMenuTooltipTextForState('duplicate-document', !!contextMenuDocument)} placement="right">
          <MenuItem 
            onClick={handleContextMenuDuplicate}
            sx={{ 
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Duplicate..." />
          </MenuItem>
        </Tooltip>
        <Tooltip title={getMenuTooltipTextForState('merge-document', !!contextMenuDocument)} placement="right">
          <MenuItem 
            onClick={handleContextMenuMerge}
            sx={{ 
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <MergeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Merge..." />
          </MenuItem>
        </Tooltip>
        <Tooltip title={getMenuTooltipTextForState('export-to-file', !!contextMenuDocument)} placement="right">
          <MenuItem 
            onClick={handleContextMenuExport}
            sx={{ 
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Export..." />
          </MenuItem>
        </Tooltip>
        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
        <Tooltip title={getMenuTooltipTextForState('delete-document', !!contextMenuDocument)} placement="right">
          <MenuItem 
            onClick={handleContextMenuDelete}
            sx={{ 
              color: '#ff6b6b',
              '&:hover': { backgroundColor: 'rgba(255, 107, 107, 0.1)' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        </Tooltip>
      </Menu>
    </StyledDrawer>
  );
};

export default DocumentPanel; 